import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  collectNavigationContext,
  computeEnvironmentHash,
  getCachedPersonalization,
  makeCacheKey,
  openSSE,
  collectEnvironmentData,
  setCachedPersonalization,
  shouldSkipPersonalize,
  supportsSSE,
  type EnvironmentData,
  type PersonalizationResponse,
  type PersonalizationSSEMessage,
  ProviderNestingError,
} from '@mitosite-ai/mitosite-core'

import { 
  MitoContext, 
  type MitoProviderContextValue, 
  type ContentUpdateListener 
} from '../context/MitoContext'

export interface MitoProviderProps {
  providerId: string
  gatewayUrl?: string
  headers?: Record<string, string>
  requestDebounceMs?: number
  onError?: (err: Error) => void
  debug?: boolean
  children?: React.ReactNode
}

const DEFAULT_GATEWAY = 'https://gateway-production-7f1e.up.railway.app'
const DEFAULT_DEBOUNCE_MS = 30

export function MitoProvider({
  providerId,
  gatewayUrl = DEFAULT_GATEWAY,
  headers,
  requestDebounceMs = DEFAULT_DEBOUNCE_MS,
  onError,
  debug,
  children,
}: MitoProviderProps) {
  // Guard against nested providers
  const parentCtx = React.useContext(MitoContext)
  if (parentCtx) {
    throw new ProviderNestingError()
  }
  const [environment, setEnvironment] = useState<EnvironmentData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const contentStore = useRef(new Map<string, string>())
  const subscribers = useRef(new Map<string, Set<ContentUpdateListener>>())
  const pendingIds = useRef(new Set<string>())
  const sseRef = useRef<ReturnType<typeof openSSE> | null>(null)
  const debounceTimer = useRef<number | null>(null)
  const lastRequestKeyRef = useRef<string | null>(null)
  const lastEnvHashRef = useRef<string | null>(null)

  const requestKeyFor = useCallback((pageUrl: string | null) => `${providerId}:${pageUrl || 'unknown'}`, [providerId])

  const notifySubscribers = useCallback((contentId: string, value: string | null) => {
    const set = subscribers.current.get(contentId)
    if (!set || set.size === 0) return
    for (const listener of set) listener(value, contentId)
  }, [])

  const scheduleFlush = useCallback(() => {
    if (debounceTimer.current) {
      window.clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }
    debounceTimer.current = window.setTimeout(() => {
      void flush()
    }, Math.max(0, requestDebounceMs))
  }, [requestDebounceMs])

  const getValue = useCallback((id: string) => contentStore.current.get(id), [])

  const subscribe = useCallback((id: string, cb: ContentUpdateListener) => {
    let set = subscribers.current.get(id)
    if (!set) {
      set = new Set()
      subscribers.current.set(id, set)
    }
    set.add(cb)
    return () => {
      const existing = subscribers.current.get(id)
      if (!existing) return
      existing.delete(cb)
      if (existing.size === 0) subscribers.current.delete(id)
    }
  }, [])

  const register = useCallback((id: string) => {
    pendingIds.current.add(id)
    scheduleFlush()
  }, [scheduleFlush])

  const closeSSE = useCallback(() => {
    try { sseRef.current?.close() } catch {}
    sseRef.current = null
  }, [])

  const flush = useCallback(async () => {
    if (!environment) return
    const ids = [...pendingIds.current]
    pendingIds.current.clear()
    if (ids.length === 0) return
    if (!supportsSSE()) {
      if (debug) console.warn('[Mitosite] SSE not supported; skipping personalize')
      return
    }

    const nav = collectNavigationContext()
    const reqKey = requestKeyFor(nav.pageUrl)
    const cacheKey = makeCacheKey(reqKey, environment, ids)
    const envHash = computeEnvironmentHash(environment)

    // Prefill from cache
    const cached = getCachedPersonalization(cacheKey)
    if (cached && cached.data) {
      for (const [k, v] of Object.entries(cached.data)) {
        contentStore.current.set(k, v as string)
        notifySubscribers(k, v as string)
      }
    }

    // Decide whether to skip streaming
    const shouldSkip = shouldSkipPersonalize(cached, {
      policy: 'skipIfComplete',
      subsetOkForSkip: true,
      requestedIds: ids,
    })
    if (shouldSkip) {
      if (debug) console.log('[Mitosite] Skipping SSE personalize due to cache policy')
      return
    }

    // If request context changed, close previous SSE
    if (lastRequestKeyRef.current !== reqKey || lastEnvHashRef.current !== envHash) {
      closeSSE()
    }

    lastRequestKeyRef.current = reqKey
    lastEnvHashRef.current = envHash

    const baseGateway = (gatewayUrl || (debug ? 'http://localhost:8093' : DEFAULT_GATEWAY))
    const url = `${baseGateway.replace(/\/$/, '')}/personalize`
    const body = {
      providerId,
      environmentData: environment,
      navigationContext: nav,
      contentIds: ids,
    }

    const sse = openSSE(url, body, 'POST', headers)
    sseRef.current = sse
    sse.onOpen(() => {
      if (debug) console.log('[Mitosite] SSE connection opened')
    })
    sse.onMessage((msg: PersonalizationSSEMessage) => {
      if (msg.type === 'update' && (msg as any).content) {
        const content = (msg as any).content as PersonalizationResponse
        for (const [cid, val] of Object.entries(content)) {
          contentStore.current.set(cid, val)
          notifySubscribers(cid, val)
        }
      } else if (msg.type === 'completed') {
        const accumulated: PersonalizationResponse = {}
        for (const id of ids) {
          const v = contentStore.current.get(id)
          if (typeof v === 'string') accumulated[id] = v
        }
        setCachedPersonalization(cacheKey, accumulated, envHash, ({ completed: true, contentIds: ids } as any))
        if (debug) console.log('[Mitosite] SSE completed, cache written')
      } else if (msg.type === 'error') {
        const err = new Error('SSE error')
        setError(err)
        onError && onError(err)
      }
    })
    sse.onError((evt: any) => {
      const err = evt instanceof Error ? evt : new Error('SSE connection error')
      setError(err)
      onError && onError(err)
    })
  }, [environment, headers, gatewayUrl, providerId, notifySubscribers, requestKeyFor, closeSSE, debug])

  const enqueueAllSubscribedIds = useCallback(() => {
    for (const id of subscribers.current.keys()) {
      pendingIds.current.add(id)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const env = await collectEnvironmentData()
        if (cancelled) return
        setEnvironment(env)
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Environment collection failed')
        setError(e)
        onError && onError(e)
      } finally {
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [onError])

  // When environment becomes available, flush any pending IDs
  useEffect(() => {
    if (!environment) return
    enqueueAllSubscribedIds()
    void flush()
  }, [environment, enqueueAllSubscribedIds, flush])

  useEffect(() => {
    const handle = () => {
      // Clear last request context so next flush opens a new SSE session for new URL
      lastRequestKeyRef.current = null
      enqueueAllSubscribedIds()
      void flush()
    }
    window.addEventListener('popstate', handle)
    window.addEventListener('hashchange', handle)
    return () => {
      window.removeEventListener('popstate', handle)
      window.removeEventListener('hashchange', handle)
    }
  }, [enqueueAllSubscribedIds, flush])

  useEffect(() => {
    return () => {
      closeSSE()
      if (debounceTimer.current) {
        window.clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }
    }
  }, [closeSSE])

  const ctx: MitoProviderContextValue = useMemo(() => ({
    getValue,
    subscribe,
    register,
    refresh: async () => { enqueueAllSubscribedIds(); await flush() },
    environment,
    loading,
    error,
  }), [getValue, subscribe, register, enqueueAllSubscribedIds, flush, environment, loading, error])

  return <MitoContext.Provider value={ctx}>{children}</MitoContext.Provider>
}


